<template>
  <section class="cotation-section">
    <div class="cotation-inner">

      <!-- ══════════════════════════════
           COLONNE GAUCHE
      ══════════════════════════════ -->
      <div class="cotation-left">

        <!-- Badge satisfaction -->
        <div class="satisfaction-badge">
          <img src="@/assets/img/avatars-group.png" alt="clients satisfaits" class="badge-avatars" />
          <span class="badge-rate">98% Satisfaction</span>
          <span class="badge-divider" aria-hidden="true"></span>
          <span class="badge-star" aria-hidden="true">⭐</span>
          <span class="badge-label">Avis très positif</span>
        </div>

        <h1 class="cotation-title">
          Le paiement<br />
          international pensé<br />
          pour les PME africaines
        </h1>

        <p class="cotation-desc">
          Nous proposons des paiements fournisseurs internationaux en EUR,
          USD et CAD, l'encaissement de clients étrangers en XOF, une
          conversion optimisée XOF, devises adaptée aux PME, des délais
          rapides (J0–J1), un support entreprise dédié ainsi qu'une conformité
          réglementaire UEMOA.
        </p>
      </div>

      <!-- ══════════════════════════════
           COLONNE DROITE — CARD FORM
      ══════════════════════════════ -->
      <div class="cotation-card">

        <div class="card-header">
          <p class="card-header__title">
            Demandez une cotation pour vos<br />paiements internationaux
          </p>
        </div>

        <!-- Skeleton Loader Overlay -->
        <div v-if="isSubmitting" class="skeleton-overlay">
          <div class="skeleton-container">
            <div class="skeleton-header"></div>
            <div class="skeleton-field"></div>
            <div class="skeleton-field"></div>
            <div class="skeleton-field"></div>
            <div class="skeleton-field"></div>
            <div class="skeleton-field"></div>
            <div class="skeleton-button"></div>
          </div>
        </div>

        <div class="card-body" :class="{ 'is-loading': isSubmitting }">

          <!-- ① Pays + Nom entreprise -->
          <div class="field-row">
            <div class="select-country" :class="{ 'field--error': errors.company }">
              <span class="fi" :class="`fi-${selectedCountry.code.toLowerCase()}`"></span>
              <select v-model="form.country" class="select-hidden" aria-label="Pays d'origine">
                <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.name }}</option>
              </select>
              <i class="fas fa-chevron-down chevron-icon" aria-hidden="true"></i>
            </div>
            <input
              v-model="form.company"
              type="text"
              class="field-input field-input--flex"
              :class="{ 'field--error': errors.company }"
              placeholder="Nom de l'entreprise *"
            />
          </div>
          <span v-if="errors.company" class="error-msg">{{ errors.company }}</span>

          <!-- ② Montant envoyé -->
          <div class="field-group">
            <label class="field-label">Montant envoyé *</label>
            <div class="field-row field-row--amount">
              <div class="select-currency">
                <span class="fi" :class="`fi-${selectedCurrency.flagCode}`"></span>
                <span class="currency-code">{{ selectedCurrency.code }}</span>
                <select v-model="form.currency" class="select-hidden" aria-label="Devise">
                  <option v-for="c in currencies" :key="c.code" :value="c.code">
                    {{ c.code }} — {{ c.name }}
                  </option>
                </select>
                <i class="fas fa-chevron-down chevron-icon" aria-hidden="true"></i>
              </div>
              <div class="amount-box" :class="{ 'field--error': errors.amount }">
                <input
                  v-model="form.amount"
                  type="number"
                  class="amount-input"
                  placeholder="10 000"
                  min="0"
                />
                <span class="amount-symbol">{{ selectedCurrency.symbol }}</span>
              </div>
            </div>
            <span v-if="errors.amount" class="error-msg">{{ errors.amount }}</span>
          </div>

          <!-- ③ Méthode de paiement -->
          <div class="field-group">
            <div 
              class="select-styled select-styled--toggle" 
              :class="{ 'is-expanded': isPaymentExpanded }"
              @click="isPaymentExpanded = !isPaymentExpanded"
            >
              <span class="select-styled__label">
                Méthode de paiement *
                <span v-if="!isPaymentExpanded && form.paymentMethod" class="select-styled__value">
                  — {{ selectedPaymentLabel }}
                </span>
              </span>
              <i 
                class="fas fa-chevron-down chevron-icon chevron-icon--toggle" 
                :class="{ 'is-rotated': isPaymentExpanded }"
                aria-hidden="true"
              ></i>
            </div>
            
            <transition name="slide-fade">
              <div v-show="isPaymentExpanded" class="payment-grid">
                <button
                  v-for="method in paymentMethods"
                  :key="method.value"
                  type="button"
                  class="payment-btn"
                  :class="{ 'is-active': form.paymentMethod === method.value }"
                  @click="form.paymentMethod = method.value; errors.paymentMethod = ''"
                >
                  <i :class="method.icon" class="payment-btn__icon" aria-hidden="true"></i>
                  <span class="payment-btn__label">{{ method.label }}</span>
                </button>
              </div>
            </transition>
            
            <span v-if="errors.paymentMethod" class="error-msg">{{ errors.paymentMethod }}</span>
          </div>

          <!-- ④ Email -->
          <div class="field-icon-wrap">
            <i class="far fa-envelope field-icon" aria-hidden="true"></i>
            <input
              v-model="form.email"
              type="email"
              class="field-input field-input--icon"
              :class="{ 'field--error': errors.email }"
              placeholder="Email *"
            />
          </div>
          <span v-if="errors.email" class="error-msg">{{ errors.email }}</span>

          <!-- ⑤ Téléphone avec indicatif -->
          <div class="field-row phone-row">
            <div class="select-country-code" :class="{ 'field--error': errors.phone, 'is-open': isPhoneCountryOpen }" @click="togglePhoneCountry">
              <span class="fi" :class="`fi-${selectedPhoneCountry.code.toLowerCase()}`"></span>
              <span class="country-code">{{ selectedPhoneCountry.dialCode }}</span>
              <i class="fas fa-chevron-down chevron-icon" :class="{ 'is-rotated': isPhoneCountryOpen }" aria-hidden="true"></i>
            </div>
            <input
              v-model="form.phone"
              type="tel"
              class="field-input field-input--flex"
              :class="{ 'field--error': errors.phone }"
              placeholder="Numéro de téléphone *"
            />
            
            <!-- Dropdown indicatifs -->
            <transition name="dropdown-slide">
              <div v-if="isPhoneCountryOpen" class="dropdown-menu dropdown-menu--phone">
                <div class="dropdown-search">
                  <i class="fas fa-search dropdown-search__icon"></i>
                  <input
                    v-model="phoneCountrySearch"
                    type="text"
                    class="dropdown-search__input"
                    placeholder="Rechercher..."
                    @click.stop
                  />
                </div>
                <div class="dropdown-list">
                  <div
                    v-for="country in filteredPhoneCountries"
                    :key="country.code"
                    class="dropdown-item"
                    :class="{ 'is-selected': form.phoneCountry === country.code }"
                    @click.stop="selectPhoneCountry(country)"
                  >
                    <span class="fi" :class="`fi-${country.code.toLowerCase()}`"></span>
                    <span class="dropdown-item__name">{{ country.name }}</span>
                    <span class="dropdown-item__code">{{ country.dialCode }}</span>
                    <i v-if="form.phoneCountry === country.code" class="fas fa-check dropdown-item__check"></i>
                  </div>
                  <div v-if="filteredPhoneCountries.length === 0" class="dropdown-empty">
                    Aucun résultat
                  </div>
                </div>
              </div>
            </transition>
          </div>
          <span v-if="errors.phone" class="error-msg">{{ errors.phone }}</span>

          <!-- ⑥ Pays de réception avec recherche -->
          <div class="field-group dropdown-wrapper">
            <div 
              class="select-styled select-styled--searchable" 
              :class="{ 'field--error': errors.receptionCountry, 'is-open': isReceptionOpen }"
              @click="toggleReception"
            >
              <div class="select-styled__inner">
                <span
                  v-if="selectedReception"
                  class="fi"
                  :class="`fi-${selectedReception.code.toLowerCase()}`"
                ></span>
                <span
                  class="select-styled__label"
                  :class="{ 'select-styled__label--filled': form.receptionCountry }"
                >
                  {{ selectedReception ? selectedReception.name : 'Pays de réception *' }}
                </span>
              </div>
              <i class="fas fa-chevron-down chevron-icon" :class="{ 'is-rotated': isReceptionOpen }" aria-hidden="true"></i>
            </div>
            
            <!-- Dropdown pays de réception -->
            <transition name="dropdown-slide">
              <div v-if="isReceptionOpen" class="dropdown-menu">
                <div class="dropdown-search">
                  <i class="fas fa-search dropdown-search__icon"></i>
                  <input
                    v-model="receptionSearch"
                    type="text"
                    class="dropdown-search__input"
                    placeholder="Rechercher un pays..."
                    @click.stop
                  />
                </div>
                <div class="dropdown-list">
                  <div
                    v-for="country in filteredReceptionCountries"
                    :key="country.code"
                    class="dropdown-item"
                    :class="{ 'is-selected': form.receptionCountry === country.code }"
                    @click.stop="selectReceptionCountry(country)"
                  >
                    <span class="fi" :class="`fi-${country.code.toLowerCase()}`"></span>
                    <span class="dropdown-item__name">{{ country.name }}</span>
                    <i v-if="form.receptionCountry === country.code" class="fas fa-check dropdown-item__check"></i>
                  </div>
                  <div v-if="filteredReceptionCountries.length === 0" class="dropdown-empty">
                    Aucun pays trouvé
                  </div>
                </div>
              </div>
            </transition>
            
            <span v-if="errors.receptionCountry" class="error-msg">{{ errors.receptionCountry }}</span>
          </div>

          <!-- ⑦ Bouton submit -->
          <div class="form-submit">
            <custom-button
              :label="isLoading ? 'Envoi en cours…' : 'Demander »»'"
              :disabled="isLoading || isSubmitting"
              @click="handleSubmit"
            />
          </div>

        </div>
      </div>
    </div>

    <!-- ══ TOAST ══ -->
    <transition name="toast-slide">
      <div
        v-if="toast.visible"
        class="toast"
        :class="`toast--${toast.type}`"
        role="alert"
        aria-live="polite"
      >
        <i :class="toast.icon" aria-hidden="true"></i>
        <span>{{ toast.message }}</span>
      </div>
    </transition>

    <!-- ══ POPUP RÉSULTAT ══ -->
    <transition name="popup-fade">
      <div v-if="popup.visible" class="popup-overlay" @click.self="closePopup">
        <div class="popup" :class="`popup--${popup.type}`">
          <div class="popup__icon-wrap">
            <i :class="popup.icon" class="popup__icon" aria-hidden="true"></i>
          </div>
          <h3 class="popup__title">{{ popup.title }}</h3>
          <p class="popup__message">{{ popup.message }}</p>
          <button class="popup__close-btn" @click="closePopup">Fermer</button>
        </div>
      </div>
    </transition>

  </section>
</template>

 

<script>
// ─── Imports ─────────────────────────────────────────────────────────────────
import CustomButton from '@/components/CustomButton.vue'
import emailjs from 'emailjs-com'
import { toast as toastNotif } from 'vue3-toastify'
import config from '@/config'

const SERVICE_ID       = config.emailjsServiceId
const TEMPLATE_ADMIN   = config.emailjsTemplateAdmin
const TEMPLATE_ADMIN_2 = config.emailjsTemplateAdmin
const TEMPLATE_CLIENT  = config.emailjsTemplateClient
// ─────────────────────────────────────────────────────────────────────────────

// ─── Liste complète des pays du monde ─────────────────────────────────────────
const ALL_COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' },
  { code: 'ZA', name: 'Afrique du Sud' },
  { code: 'AL', name: 'Albanie' },
  { code: 'DZ', name: 'Algérie' },
  { code: 'DE', name: 'Allemagne' },
  { code: 'AD', name: 'Andorre' },
  { code: 'AO', name: 'Angola' },
  { code: 'AG', name: 'Antigua-et-Barbuda' },
  { code: 'SA', name: 'Arabie saoudite' },
  { code: 'AR', name: 'Argentine' },
  { code: 'AM', name: 'Arménie' },
  { code: 'AU', name: 'Australie' },
  { code: 'AT', name: 'Autriche' },
  { code: 'AZ', name: 'Azerbaïdjan' },
  { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahreïn' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'BB', name: 'Barbade' },
  { code: 'BE', name: 'Belgique' },
  { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Bénin' },
  { code: 'BT', name: 'Bhoutan' },
  { code: 'BY', name: 'Biélorussie' },
  { code: 'MM', name: 'Birmanie' },
  { code: 'BO', name: 'Bolivie' },
  { code: 'BA', name: 'Bosnie-Herzégovine' },
  { code: 'BW', name: 'Botswana' },
  { code: 'BR', name: 'Brésil' },
  { code: 'BN', name: 'Brunei' },
  { code: 'BG', name: 'Bulgarie' },
  { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' },
  { code: 'KH', name: 'Cambodge' },
  { code: 'CM', name: 'Cameroun' },
  { code: 'CA', name: 'Canada' },
  { code: 'CV', name: 'Cap-Vert' },
  { code: 'CF', name: 'Centrafrique' },
  { code: 'CL', name: 'Chili' },
  { code: 'CN', name: 'Chine' },
  { code: 'CY', name: 'Chypre' },
  { code: 'CO', name: 'Colombie' },
  { code: 'KM', name: 'Comores' },
  { code: 'CG', name: 'Congo' },
  { code: 'CD', name: 'Congo (RDC)' },
  { code: 'KR', name: 'Corée du Sud' },
  { code: 'KP', name: 'Corée du Nord' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'CI', name: "Côte d'Ivoire" },
  { code: 'HR', name: 'Croatie' },
  { code: 'CU', name: 'Cuba' },
  { code: 'DK', name: 'Danemark' },
  { code: 'DJ', name: 'Djibouti' },
  { code: 'DM', name: 'Dominique' },
  { code: 'EG', name: 'Égypte' },
  { code: 'AE', name: 'Émirats arabes unis' },
  { code: 'EC', name: 'Équateur' },
  { code: 'ER', name: 'Érythrée' },
  { code: 'ES', name: 'Espagne' },
  { code: 'EE', name: 'Estonie' },
  { code: 'SZ', name: 'Eswatini' },
  { code: 'US', name: 'États-Unis' },
  { code: 'ET', name: 'Éthiopie' },
  { code: 'FJ', name: 'Fidji' },
  { code: 'FI', name: 'Finlande' },
  { code: 'FR', name: 'France' },
  { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambie' },
  { code: 'GE', name: 'Géorgie' },
  { code: 'GH', name: 'Ghana' },
  { code: 'GR', name: 'Grèce' },
  { code: 'GD', name: 'Grenade' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'GN', name: 'Guinée' },
  { code: 'GQ', name: 'Guinée équatoriale' },
  { code: 'GW', name: 'Guinée-Bissau' },
  { code: 'GY', name: 'Guyana' },
  { code: 'HT', name: 'Haïti' },
  { code: 'HN', name: 'Honduras' },
  { code: 'HU', name: 'Hongrie' },
  { code: 'IN', name: 'Inde' },
  { code: 'ID', name: 'Indonésie' },
  { code: 'IQ', name: 'Irak' },
  { code: 'IR', name: 'Iran' },
  { code: 'IE', name: 'Irlande' },
  { code: 'IS', name: 'Islande' },
  { code: 'IL', name: 'Israël' },
  { code: 'IT', name: 'Italie' },
  { code: 'JM', name: 'Jamaïque' },
  { code: 'JP', name: 'Japon' },
  { code: 'JO', name: 'Jordanie' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'KE', name: 'Kenya' },
  { code: 'KG', name: 'Kirghizistan' },
  { code: 'KI', name: 'Kiribati' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'KW', name: 'Koweït' },
  { code: 'LA', name: 'Laos' },
  { code: 'LS', name: 'Lesotho' },
  { code: 'LV', name: 'Lettonie' },
  { code: 'LB', name: 'Liban' },
  { code: 'LR', name: 'Libéria' },
  { code: 'LY', name: 'Libye' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lituanie' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MK', name: 'Macédoine du Nord' },
  { code: 'MG', name: 'Madagascar' },
  { code: 'MY', name: 'Malaisie' },
  { code: 'MW', name: 'Malawi' },
  { code: 'MV', name: 'Maldives' },
  { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malte' },
  { code: 'MA', name: 'Maroc' },
  { code: 'MH', name: 'Marshall' },
  { code: 'MR', name: 'Mauritanie' },
  { code: 'MU', name: 'Maurice' },
  { code: 'MX', name: 'Mexique' },
  { code: 'FM', name: 'Micronésie' },
  { code: 'MD', name: 'Moldavie' },
  { code: 'MC', name: 'Monaco' },
  { code: 'MN', name: 'Mongolie' },
  { code: 'ME', name: 'Monténégro' },
  { code: 'MZ', name: 'Mozambique' },
  { code: 'NA', name: 'Namibie' },
  { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Népal' },
  { code: 'NI', name: 'Nicaragua' },
  { code: 'NE', name: 'Niger' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'NO', name: 'Norvège' },
  { code: 'NZ', name: 'Nouvelle-Zélande' },
  { code: 'OM', name: 'Oman' },
  { code: 'UG', name: 'Ouganda' },
  { code: 'UZ', name: 'Ouzbékistan' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'PW', name: 'Palaos' },
  { code: 'PA', name: 'Panama' },
  { code: 'PG', name: 'Papouasie-Nouvelle-Guinée' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'NL', name: 'Pays-Bas' },
  { code: 'PE', name: 'Pérou' },
  { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Pologne' },
  { code: 'PT', name: 'Portugal' },
  { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Roumanie' },
  { code: 'GB', name: 'Royaume-Uni' },
  { code: 'RU', name: 'Russie' },
  { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint-Christophe-et-Niévès' },
  { code: 'SM', name: 'Saint-Marin' },
  { code: 'VC', name: 'Saint-Vincent-et-les-Grenadines' },
  { code: 'LC', name: 'Sainte-Lucie' },
  { code: 'SV', name: 'Salvador' },
  { code: 'WS', name: 'Samoa' },
  { code: 'ST', name: 'Sao Tomé-et-Principe' },
  { code: 'SN', name: 'Sénégal' },
  { code: 'RS', name: 'Serbie' },
  { code: 'SC', name: 'Seychelles' },
  { code: 'SL', name: 'Sierra Leone' },
  { code: 'SG', name: 'Singapour' },
  { code: 'SK', name: 'Slovaquie' },
  { code: 'SI', name: 'Slovénie' },
  { code: 'SO', name: 'Somalie' },
  { code: 'SD', name: 'Soudan' },
  { code: 'SS', name: 'Soudan du Sud' },
  { code: 'LK', name: 'Sri Lanka' },
  { code: 'SE', name: 'Suède' },
  { code: 'CH', name: 'Suisse' },
  { code: 'SR', name: 'Suriname' },
  { code: 'SY', name: 'Syrie' },
  { code: 'TJ', name: 'Tadjikistan' },
  { code: 'TZ', name: 'Tanzanie' },
  { code: 'TD', name: 'Tchad' },
  { code: 'CZ', name: 'Tchéquie' },
  { code: 'TH', name: 'Thaïlande' },
  { code: 'TL', name: 'Timor oriental' },
  { code: 'TG', name: 'Togo' },
  { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinité-et-Tobago' },
  { code: 'TN', name: 'Tunisie' },
  { code: 'TM', name: 'Turkménistan' },
  { code: 'TR', name: 'Turquie' },
  { code: 'TV', name: 'Tuvalu' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'VN', name: 'Viêt Nam' },
  { code: 'YE', name: 'Yémen' },
  { code: 'ZM', name: 'Zambie' },
  { code: 'ZW', name: 'Zimbabwe' }
]

// ─── Pays avec indicatifs téléphoniques ───────────────────────────────────────
const PHONE_COUNTRIES = [
  { code: 'SN', name: 'Sénégal', dialCode: '+221' },
  { code: 'BJ', name: 'Bénin', dialCode: '+229' },
  { code: 'TG', name: 'Togo', dialCode: '+228' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225' },
  { code: 'ML', name: 'Mali', dialCode: '+223' },
  { code: 'BF', name: 'Burkina Faso', dialCode: '+226' },
  { code: 'GN', name: 'Guinée', dialCode: '+224' },
  { code: 'CM', name: 'Cameroun', dialCode: '+237' },
  { code: 'GA', name: 'Gabon', dialCode: '+241' },
  { code: 'FR', name: 'France', dialCode: '+33' },
  { code: 'US', name: 'États-Unis', dialCode: '+1' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49' },
  { code: 'IT', name: 'Italie', dialCode: '+39' },
  { code: 'ES', name: 'Espagne', dialCode: '+34' },
  { code: 'PT', name: 'Portugal', dialCode: '+351' },
  { code: 'BE', name: 'Belgique', dialCode: '+32' },
  { code: 'CH', name: 'Suisse', dialCode: '+41' },
  { code: 'CA', name: 'Canada', dialCode: '+1' },
  { code: 'CN', name: 'Chine', dialCode: '+86' },
  { code: 'IN', name: 'Inde', dialCode: '+91' },
  { code: 'JP', name: 'Japon', dialCode: '+81' },
  { code: 'BR', name: 'Brésil', dialCode: '+55' },
  { code: 'RU', name: 'Russie', dialCode: '+7' },
  { code: 'ZA', name: 'Afrique du Sud', dialCode: '+27' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234' },
  { code: 'GH', name: 'Ghana', dialCode: '+233' },
  { code: 'KE', name: 'Kenya', dialCode: '+254' },
  { code: 'TZ', name: 'Tanzanie', dialCode: '+255' },
  { code: 'UG', name: 'Ouganda', dialCode: '+256' },
  { code: 'RW', name: 'Rwanda', dialCode: '+250' },
  { code: 'CD', name: 'RDC', dialCode: '+243' },
  { code: 'CG', name: 'Congo', dialCode: '+242' },
  { code: 'GQ', name: 'Guinée équatoriale', dialCode: '+240' },
  { code: 'GW', name: 'Guinée-Bissau', dialCode: '+245' },
  { code: 'LR', name: 'Libéria', dialCode: '+231' },
  { code: 'SL', name: 'Sierra Leone', dialCode: '+232' },
  { code: 'GM', name: 'Gambie', dialCode: '+220' },
  { code: 'MR', name: 'Mauritanie', dialCode: '+222' },
  { code: 'NE', name: 'Niger', dialCode: '+227' },
  { code: 'TD', name: 'Tchad', dialCode: '+235' },
  { code: 'CF', name: 'Centrafrique', dialCode: '+236' },
  { code: 'ST', name: 'Sao Tomé-et-Principe', dialCode: '+239' },
  { code: 'CV', name: 'Cap-Vert', dialCode: '+238' },
  { code: 'MA', name: 'Maroc', dialCode: '+212' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216' },
  { code: 'LY', name: 'Libye', dialCode: '+218' },
  { code: 'EG', name: 'Égypte', dialCode: '+20' },
  { code: 'SD', name: 'Soudan', dialCode: '+249' },
  { code: 'ET', name: 'Éthiopie', dialCode: '+251' },
  { code: 'ER', name: 'Érythrée', dialCode: '+291' },
  { code: 'DJ', name: 'Djibouti', dialCode: '+253' },
  { code: 'SO', name: 'Somalie', dialCode: '+252' },
  { code: 'BI', name: 'Burundi', dialCode: '+257' },
  { code: 'MZ', name: 'Mozambique', dialCode: '+258' },
  { code: 'ZM', name: 'Zambie', dialCode: '+260' },
  { code: 'MG', name: 'Madagascar', dialCode: '+261' },
  { code: 'ZW', name: 'Zimbabwe', dialCode: '+263' },
  { code: 'NA', name: 'Namibie', dialCode: '+264' },
  { code: 'MW', name: 'Malawi', dialCode: '+265' },
  { code: 'LS', name: 'Lesotho', dialCode: '+266' },
  { code: 'BW', name: 'Botswana', dialCode: '+267' },
  { code: 'SZ', name: 'Eswatini', dialCode: '+268' },
  { code: 'KM', name: 'Comores', dialCode: '+269' },
  { code: 'RU', name: 'Russie', dialCode: '+7' },
  { code: 'KZ', name: 'Kazakhstan', dialCode: '+7' },
  { code: 'GR', name: 'Grèce', dialCode: '+30' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '+31' },
  { code: 'LU', name: 'Luxembourg', dialCode: '+352' },
  { code: 'IE', name: 'Irlande', dialCode: '+353' },
  { code: 'IS', name: 'Islande', dialCode: '+354' },
  { code: 'AL', name: 'Albanie', dialCode: '+355' },
  { code: 'MT', name: 'Malte', dialCode: '+356' },
  { code: 'CY', name: 'Chypre', dialCode: '+357' },
  { code: 'FI', name: 'Finlande', dialCode: '+358' },
  { code: 'BG', name: 'Bulgarie', dialCode: '+359' },
  { code: 'HU', name: 'Hongrie', dialCode: '+36' },
  { code: 'LT', name: 'Lituanie', dialCode: '+370' },
  { code: 'LV', name: 'Lettonie', dialCode: '+371' },
  { code: 'EE', name: 'Estonie', dialCode: '+372' },
  { code: 'MD', name: 'Moldavie', dialCode: '+373' },
  { code: 'AM', name: 'Arménie', dialCode: '+374' },
  { code: 'BY', name: 'Biélorussie', dialCode: '+375' },
  { code: 'AD', name: 'Andorre', dialCode: '+376' },
  { code: 'MC', name: 'Monaco', dialCode: '+377' },
  { code: 'SM', name: 'Saint-Marin', dialCode: '+378' },
  { code: 'VA', name: 'Vatican', dialCode: '+379' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380' },
  { code: 'RS', name: 'Serbie', dialCode: '+381' },
  { code: 'ME', name: 'Monténégro', dialCode: '+382' },
  { code: 'XK', name: 'Kosovo', dialCode: '+383' },
  { code: 'HR', name: 'Croatie', dialCode: '+385' },
  { code: 'SI', name: 'Slovénie', dialCode: '+386' },
  { code: 'BA', name: 'Bosnie-Herzégovine', dialCode: '+387' },
  { code: 'MK', name: 'Macédoine du Nord', dialCode: '+389' },
  { code: 'CZ', name: 'Tchéquie', dialCode: '+420' },
  { code: 'SK', name: 'Slovaquie', dialCode: '+421' },
  { code: 'LI', name: 'Liechtenstein', dialCode: '+423' },
  { code: 'AT', name: 'Autriche', dialCode: '+43' },
  { code: 'DK', name: 'Danemark', dialCode: '+45' },
  { code: 'SE', name: 'Suède', dialCode: '+46' },
  { code: 'NO', name: 'Norvège', dialCode: '+47' },
  { code: 'PL', name: 'Pologne', dialCode: '+48' },
  { code: 'BZ', name: 'Belize', dialCode: '+501' },
  { code: 'GT', name: 'Guatemala', dialCode: '+502' },
  { code: 'SV', name: 'Salvador', dialCode: '+503' },
  { code: 'HN', name: 'Honduras', dialCode: '+504' },
  { code: 'NI', name: 'Nicaragua', dialCode: '+505' },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506' },
  { code: 'PA', name: 'Panama', dialCode: '+507' },
  { code: 'HT', name: 'Haïti', dialCode: '+509' },
  { code: 'BO', name: 'Bolivie', dialCode: '+591' },
  { code: 'GY', name: 'Guyana', dialCode: '+592' },
  { code: 'EC', name: 'Équateur', dialCode: '+593' },
  { code: 'PY', name: 'Paraguay', dialCode: '+595' },
  { code: 'SR', name: 'Suriname', dialCode: '+597' },
  { code: 'UY', name: 'Uruguay', dialCode: '+598' },
  { code: 'MY', name: 'Malaisie', dialCode: '+60' },
  { code: 'AU', name: 'Australie', dialCode: '+61' },
  { code: 'ID', name: 'Indonésie', dialCode: '+62' },
  { code: 'PH', name: 'Philippines', dialCode: '+63' },
  { code: 'NZ', name: 'Nouvelle-Zélande', dialCode: '+64' },
  { code: 'SG', name: 'Singapour', dialCode: '+65' },
  { code: 'TH', name: 'Thaïlande', dialCode: '+66' },
  { code: 'BN', name: 'Brunei', dialCode: '+673' },
  { code: 'NR', name: 'Nauru', dialCode: '+674' },
  { code: 'PG', name: 'Papouasie-Nouvelle-Guinée', dialCode: '+675' },
  { code: 'TO', name: 'Tonga', dialCode: '+676' },
  { code: 'SB', name: 'Îles Salomon', dialCode: '+677' },
  { code: 'VU', name: 'Vanuatu', dialCode: '+678' },
  { code: 'FJ', name: 'Fidji', dialCode: '+679' },
  { code: 'PW', name: 'Palaos', dialCode: '+680' },
  { code: 'WS', name: 'Samoa', dialCode: '+685' },
  { code: 'KI', name: 'Kiribati', dialCode: '+686' },
  { code: 'TV', name: 'Tuvalu', dialCode: '+688' },
  { code: 'FM', name: 'Micronésie', dialCode: '+691' },
  { code: 'MH', name: 'Marshall', dialCode: '+692' },
  { code: 'KP', name: 'Corée du Nord', dialCode: '+850' },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852' },
  { code: 'MO', name: 'Macao', dialCode: '+853' },
  { code: 'KH', name: 'Cambodge', dialCode: '+855' },
  { code: 'LA', name: 'Laos', dialCode: '+856' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880' },
  { code: 'TW', name: 'Taïwan', dialCode: '+886' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92' },
  { code: 'AF', name: 'Afghanistan', dialCode: '+93' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94' },
  { code: 'MM', name: 'Birmanie', dialCode: '+95' },
  { code: 'MV', name: 'Maldives', dialCode: '+960' },
  { code: 'LB', name: 'Liban', dialCode: '+961' },
  { code: 'JO', name: 'Jordanie', dialCode: '+962' },
  { code: 'SY', name: 'Syrie', dialCode: '+963' },
  { code: 'IQ', name: 'Irak', dialCode: '+964' },
  { code: 'KW', name: 'Koweït', dialCode: '+965' },
  { code: 'SA', name: 'Arabie saoudite', dialCode: '+966' },
  { code: 'YE', name: 'Yémen', dialCode: '+967' },
  { code: 'OM', name: 'Oman', dialCode: '+968' },
  { code: 'PS', name: 'Palestine', dialCode: '+970' },
  { code: 'AE', name: 'Émirats arabes unis', dialCode: '+971' },
  { code: 'IL', name: 'Israël', dialCode: '+972' },
  { code: 'BH', name: 'Bahreïn', dialCode: '+973' },
  { code: 'QA', name: 'Qatar', dialCode: '+974' },
  { code: 'BT', name: 'Bhoutan', dialCode: '+975' },
  { code: 'MN', name: 'Mongolie', dialCode: '+976' },
  { code: 'NP', name: 'Népal', dialCode: '+977' },
  { code: 'IR', name: 'Iran', dialCode: '+98' },
  { code: 'TJ', name: 'Tadjikistan', dialCode: '+992' },
  { code: 'TM', name: 'Turkménistan', dialCode: '+993' },
  { code: 'AZ', name: 'Azerbaïdjan', dialCode: '+994' },
  { code: 'GE', name: 'Géorgie', dialCode: '+995' },
  { code: 'KG', name: 'Kirghizistan', dialCode: '+996' },
  { code: 'UZ', name: 'Ouzbékistan', dialCode: '+998' }
]

export default {
  name: 'CotationSection',

  components: {
    CustomButton,
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      isPaymentExpanded: true,
      submitLock: false,
      
      // ── Formulaire ──────────────────────────────────────────────────────────
      form: {
        country:          'SN',
        company:          '',
        currency:         'EUR',
        amount:           '',
        paymentMethod:    'cash',
        email:            '',
        phone:            '',
        phoneCountry:     'SN',
        receptionCountry: '',
      },

      // ── Erreurs inline ──────────────────────────────────────────────────────
      errors: {
        company: '', amount: '', paymentMethod: '',
        email: '', phone: '', receptionCountry: '',
      },

      // ── Pays d'origine (code ISO 3166-1 alpha-2 → utilisé par flag-icons) ──
      countries: ALL_COUNTRIES,

      // ── Devises ─────────────────────────────────────────────────────────────
      currencies: [
        { code: 'EUR', name: 'Euro',           flagCode: 'eu', symbol: '€'    },
        { code: 'USD', name: 'Dollar US',      flagCode: 'us', symbol: '$'    },
        { code: 'CAD', name: 'Dollar canadien',flagCode: 'ca', symbol: 'CA$'  },
        { code: 'GBP', name: 'Livre Sterling', flagCode: 'gb', symbol: '£'    },
        { code: 'CNY', name: 'Yuan chinois',   flagCode: 'cn', symbol: '¥'    },
        { code: 'AED', name: 'Dirham EAU',     flagCode: 'ae', symbol: 'د.إ' },
        { code: 'XOF', name: 'Franc CFA',      flagCode: 'sn', symbol: 'CFA'  },
      ],

      // ── Pays de réception (tous les pays du monde) ──────────────────────────
      receptionCountries: ALL_COUNTRIES,

      // ── Pays pour indicatifs téléphoniques ──────────────────────────────────
      phoneCountries: PHONE_COUNTRIES,

      // ── Méthodes de paiement (icônes Font Awesome) ──────────────────────────
      paymentMethods: [
        { value: 'mobile',   label: 'Mobile Money', icon: 'fas fa-mobile-alt'      },
        { value: 'virement', label: 'Virement',      icon: 'fas fa-exchange-alt'    },
        { value: 'cash',     label: 'Cash',          icon: 'fas fa-money-bill-wave' },
      ],

      // ── État des dropdowns ──────────────────────────────────────────────────
      isReceptionOpen: false,
      isPhoneCountryOpen: false,
      receptionSearch: '',
      phoneCountrySearch: '',

      // ── Toast ────────────────────────────────────────────────────────────────
      toast: {
        visible: false,
        type:    'info',
        icon:    '',
        message: '',
        _timer:  null,
      },

      // ── Popup ────────────────────────────────────────────────────────────────
      popup: {
        visible: false,
        type:    'success',
        icon:    '',
        title:   '',
        message: '',
        hasRetry: false,
      },
    }
  },

  computed: {
    selectedCountry() {
      return this.countries.find(c => c.code === this.form.country) || this.countries[0]
    },
    selectedCurrency() {
      return this.currencies.find(c => c.code === this.form.currency) || this.currencies[0]
    },
    selectedReception() {
      return this.receptionCountries.find(c => c.code === this.form.receptionCountry) || null
    },
    selectedPaymentLabel() {
      return this.paymentMethods.find(m => m.value === this.form.paymentMethod)?.label || ''
    },
    selectedPhoneCountry() {
      return this.phoneCountries.find(c => c.code === this.form.phoneCountry) || this.phoneCountries[0]
    },
    filteredReceptionCountries() {
      if (!this.receptionSearch) return this.receptionCountries
      const search = this.receptionSearch.toLowerCase()
      return this.receptionCountries.filter(c => 
        c.name.toLowerCase().includes(search) || 
        c.code.toLowerCase().includes(search)
      )
    },
    filteredPhoneCountries() {
      if (!this.phoneCountrySearch) return this.phoneCountries
      const search = this.phoneCountrySearch.toLowerCase()
      return this.phoneCountries.filter(c => 
        c.name.toLowerCase().includes(search) || 
        c.dialCode.includes(search)
      )
    },
  },

  mounted() {
    // Fermer les dropdowns quand on clique en dehors
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    /* ── Gestion des clics extérieurs ───────────────────────────────────────── */
    handleClickOutside(event) {
      const receptionDropdown = this.$el.querySelector('.dropdown-wrapper')
      const phoneDropdown = this.$el.querySelector('.phone-row')
      
      if (receptionDropdown && !receptionDropdown.contains(event.target)) {
        this.isReceptionOpen = false
      }
      if (phoneDropdown && !phoneDropdown.contains(event.target)) {
        this.isPhoneCountryOpen = false
      }
    },

    /* ── Gestion des dropdowns ──────────────────────────────────────────────── */
    toggleReception() {
      this.isReceptionOpen = !this.isReceptionOpen
      if (this.isReceptionOpen) {
        this.isPhoneCountryOpen = false
        this.receptionSearch = ''
        this.$nextTick(() => {
          const input = this.$el.querySelector('.dropdown-menu:not(.dropdown-menu--phone) .dropdown-search__input')
          input?.focus()
        })
      }
    },
    selectReceptionCountry(country) {
      this.form.receptionCountry = country.code
      this.errors.receptionCountry = ''
      this.isReceptionOpen = false
    },
    togglePhoneCountry() {
      this.isPhoneCountryOpen = !this.isPhoneCountryOpen
      if (this.isPhoneCountryOpen) {
        this.isReceptionOpen = false
        this.phoneCountrySearch = ''
        this.$nextTick(() => {
          const input = this.$el.querySelector('.dropdown-menu--phone .dropdown-search__input')
          input?.focus()
        })
      }
    },
    selectPhoneCountry(country) {
      this.form.phoneCountry = country.code
      this.isPhoneCountryOpen = false
    },

    /* ── Popup ──────────────────────────────────────────────────────────────── */
    showPopup(type, title, message) {
      this.popup = {
        visible: true,
        type,
        icon:    type === 'success' ? 'fas fa-check-circle' : 'fas fa-times-circle',
        title,
        message,
      }
    },

    closePopup() {
      this.popup.visible = false
    },

    /* ── Validation ─────────────────────────────────────────────────────────── */
    validate() {
      Object.keys(this.errors).forEach(k => { this.errors[k] = '' })
      let valid = true

      if (!this.form.company.trim()) {
        this.errors.company = "Le nom de l'entreprise est requis."
        valid = false
      }
      if (!this.form.amount || Number(this.form.amount) <= 0) {
        this.errors.amount = 'Veuillez saisir un montant valide.'
        valid = false
      }
      if (!this.form.paymentMethod) {
        this.errors.paymentMethod = 'Veuillez choisir une méthode de paiement.'
        valid = false
      }
      if (!this.form.email.trim() || !/\S+@\S+\.\S+/.test(this.form.email)) {
        this.errors.email = 'Veuillez saisir un email valide.'
        valid = false
      }
      if (!this.form.phone.trim() || this.form.phone.replace(/\D/g, '').length < 8) {
        this.errors.phone = 'Veuillez saisir un numéro valide (min. 8 chiffres).'
        valid = false
      }
      if (!this.form.receptionCountry) {
        this.errors.receptionCountry = 'Veuillez choisir un pays de réception.'
        valid = false
      }

      return valid
    },

    /* ── Soumission EmailJS ─────────────────────────────────────────────────── */
    async handleSubmit() {
      // Verrou anti-double soumission
      if (this.submitLock || this.isSubmitting) {
        console.log('Soumission déjà en cours, ignorée')
        return
      }

      if (!this.validate()) {
        toastNotif.error('Veuillez corriger les erreurs dans le formulaire.', {
          position: toastNotif.POSITION.BOTTOM_RIGHT,
          theme: 'dark',
        })
        return
      }

      // Activer les verrous
      this.submitLock = true
      this.isSubmitting = true
      this.isLoading = true

      let loadingToastId = null
      
      try {
        // Toast de chargement avec vue3-toastify
        loadingToastId = toastNotif.loading('Envoi de votre demande en cours…', {
          position: toastNotif.POSITION.BOTTOM_RIGHT,
          theme: 'dark',
        })

        const dateDemande = new Date().toLocaleDateString('fr-FR', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })

        // ── Envoi 1 : Mail admin principal ──
        await emailjs.send(SERVICE_ID, TEMPLATE_ADMIN, {
          to_email:         'hello@nolido.cash',
          date_demande:     dateDemande,
          entreprise:       this.form.company,
          pays_origine:     this.selectedCountry.name,
          pays_reception:   this.selectedReception?.name || '',
          montant:          this.form.amount,
          montant_formate:  `${Number(this.form.amount).toLocaleString('fr-FR')} ${this.form.currency}`,
          devise:           this.form.currency,
          methode_paiement: this.selectedPaymentLabel,
          email_client:     String(this.form.email),
          telephone:        `${this.selectedPhoneCountry.dialCode} ${this.form.phone}`,
        })

        // ── Envoi 2 : Mail admin secondaire ──
        await emailjs.send(SERVICE_ID, TEMPLATE_ADMIN_2, {
          to_email:         'marcosseko.travail@gmail.com',
          date_demande:     dateDemande,
          entreprise:       this.form.company,
          pays_origine:     this.selectedCountry.name,
          pays_reception:   this.selectedReception?.name || '',
          montant:          this.form.amount,
          montant_formate:  `${Number(this.form.amount).toLocaleString('fr-FR')} ${this.form.currency}`,
          devise:           this.form.currency,
          methode_paiement: this.selectedPaymentLabel,
          email_client:     String(this.form.email),
          telephone:        `${this.selectedPhoneCountry.dialCode} ${this.form.phone}`,
        })

        // ── Envoi 3 : Email de confirmation au client ──
        await emailjs.send(SERVICE_ID, TEMPLATE_CLIENT, {
          date_demande:     dateDemande,
          entreprise:       this.form.company,
          pays_origine:     this.selectedCountry.name,
          pays_reception:   this.selectedReception?.name || '',
          montant:          this.form.amount,
          montant_formate:  `${Number(this.form.amount).toLocaleString('fr-FR')} ${this.form.currency}`,
          devise:           this.form.currency,
          methode_paiement: this.selectedPaymentLabel,
          to_email:         String(this.form.email),
          email_client:     String(this.form.email),
          telephone:        `${this.selectedPhoneCountry.dialCode} ${this.form.phone}`,
        })

        // Succès - retirer le toast de chargement
        if (loadingToastId) {
          toastNotif.remove(loadingToastId)
        }
        
        // Toast de succès rapide (ne remplace pas la popup)
        toastNotif.success('Demande envoyée !', {
          position: toastNotif.POSITION.BOTTOM_RIGHT,
          theme: 'dark',
          autoClose: 2000,
        })

        // Afficher la popup de succès (conservée)
        this.showPopup(
          'success',
          'Demande reçue ✅',
          `Merci ${this.form.company} ! Votre demande de cotation a bien été enregistrée. Un email de confirmation vous a été envoyé à ${this.form.email}. Un expert Nolido FX vous contactera très prochainement.`
        )
        
        this.resetForm()

      } catch (err) {
        console.error('EmailJS error →', err)
        
        // Retirer le toast de chargement en cas d'erreur
        if (loadingToastId) {
          toastNotif.remove(loadingToastId)
        }
        
        const msg = err?.text || "Une erreur est survenue lors de l'envoi. Veuillez réessayer."
        
        // Toast d'erreur (ne remplace pas la popup)
        toastNotif.error('Échec de l\'envoi', {
          position: toastNotif.POSITION.BOTTOM_RIGHT,
          theme: 'dark',
        })
        
        // Afficher la popup d'erreur (conservée)
        this.showPopup('error', 'Échec de l\'envoi ❌', msg)
        
      } finally {
        // Toujours réinitialiser les états, même en cas d'erreur réseau
        this.isSubmitting = false
        this.isLoading = false
        
        // Délai avant de réactiver le verrou pour éviter les doubles clics accidentels
        setTimeout(() => {
          this.submitLock = false
        }, 1000)
      }
    },

    /* ── Reset formulaire ───────────────────────────────────────────────────── */
    resetForm() {
      this.form = {
        country: 'SN', 
        company: '', 
        currency: 'EUR', 
        amount: '',
        paymentMethod: 'cash', 
        email: '', 
        phone: '', 
        phoneCountry: 'SN',
        receptionCountry: '',
      }
      Object.keys(this.errors).forEach(k => { this.errors[k] = '' })
      this.isPaymentExpanded = true
    },
  },
}
</script>

<style scoped>
/* ════════════════════════════════════════════════
   SKELETON LOADER
════════════════════════════════════════════════ */
.skeleton-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  z-index: 100;
  border-radius: var(--radius-card);
  display: flex;
  align-items: center;
  justify-content: center;
}

.skeleton-container {
  width: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-header {
  height: 60px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: var(--radius-field);
  margin-bottom: 10px;
}

.skeleton-field {
  height: 48px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: var(--radius-field);
}

.skeleton-button {
  height: 48px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 100px;
  margin-top: 10px;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.card-body.is-loading {
  pointer-events: none;
  opacity: 0.6;
}

/* ════════════════════════════════════════════════
   DROPDOWNS
════════════════════════════════════════════════ */
.dropdown-wrapper {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-field);
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 1000;
  max-height: 320px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.dropdown-menu--phone {
  left: 0;
  right: auto;
  min-width: 280px;
  max-width: 320px;
}

.dropdown-search {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.dropdown-search__icon {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-muted);
  font-size: 0.85rem;
}

.dropdown-search__input {
  width: 100%;
  height: 36px;
  padding: 0 12px 0 32px;
  border: 1.5px solid var(--color-border);
  border-radius: 8px;
  font-family: var(--font);
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.2s;
}

.dropdown-search__input:focus {
  border-color: var(--color-accent);
}

.dropdown-list {
  overflow-y: auto;
  max-height: 240px;
  padding: 4px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.dropdown-item:hover {
  background-color: #faf8f5;
}

.dropdown-item.is-selected {
  background-color: rgba(200, 149, 43, 0.1);
}

.dropdown-item__name {
  flex: 1;
  font-size: 0.875rem;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-item__code {
  font-size: 0.8rem;
  color: var(--color-muted);
  font-weight: 600;
  flex-shrink: 0;
}

.dropdown-item__check {
  color: var(--color-accent);
  font-size: 0.85rem;
  flex-shrink: 0;
}

.dropdown-empty {
  padding: 24px;
  text-align: center;
  color: var(--color-muted);
  font-size: 0.875rem;
}

/* Animation dropdown */
.dropdown-slide-enter-active,
.dropdown-slide-leave-active {
  transition: all 0.2s ease;
  transform-origin: top;
}

.dropdown-slide-enter-from,
.dropdown-slide-leave-to {
  opacity: 0;
  transform: scaleY(0.95) translateY(-10px);
}

/* ════════════════════════════════════════════════
   PHONE INPUT
════════════════════════════════════════════════ */
.phone-row {
  position: relative;
  gap: 8px;
}

.select-country-code {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: var(--h-field);
  min-width: 100px;
  border: 1.5px solid var(--color-border);
  border-radius: var(--radius-field);
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.select-country-code:hover,
.select-country-code.is-open {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px rgba(243, 193, 125, 0.15);
}

.country-code {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text);
}

/* Select searchable */
.select-styled--searchable {
  cursor: pointer;
}

.select-styled--searchable.is-open {
  border-color: var(--color-accent);
}

/* ════════════════════════════════════════════════
   TOGGLE MÉTHODE DE PAIEMENT
════════════════════════════════════════════════ */
.select-styled--toggle {
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
}

.select-styled--toggle:hover {
  border-color: var(--color-accent);
  background-color: #faf8f5;
}

.select-styled--toggle.is-expanded {
  border-color: var(--color-accent);
  background-color: #fff;
}

.select-styled__value {
  color: var(--color-text);
  font-weight: 600;
  margin-left: 4px;
}

.chevron-icon--toggle {
  transition: transform 0.3s ease;
}

.chevron-icon.is-rotated {
  transform: rotate(180deg);
}

/* Animation slide */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
  max-height: 200px;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
  max-height: 200px;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
  overflow: hidden;
}

/* Ajustement pour la grid quand elle est cachée */
.payment-grid {
  overflow: hidden;
}

/* ════════════════════════════════════════════════
   TOKENS
════════════════════════════════════════════════ */
.cotation-section {
  --font:           'Sora', 'DM Sans', system-ui, sans-serif;
  --color-text:     #1a1100;
  --color-muted:    #666666;
  --color-border:   #E2DDD6;
  --color-error:    #d32f2f;
  --color-accent:   #c8952b;
  --color-card-hdr: #2A1A08;
  --radius-card:    20px;
  --radius-field:   10px;
  --h-field:        48px;
}

/* ════════════════════════════════════════════════
   SECTION
════════════════════════════════════════════════ */
.cotation-section {
  position: relative;
  padding: 72px 50px 80px;
  background: #fff;
  font-family: var(--font);
}

.cotation-inner {
  display: grid;
  grid-template-columns: 1fr 1.05fr;
  gap: 64px;
  align-items: start;
  max-width: 1100px;
  margin: 0 auto;
}

/* ════════════════════════════════════════════════
   COLONNE GAUCHE
════════════════════════════════════════════════ */
.cotation-left { display: flex; flex-direction: column; gap: 24px; padding-top: 4px; }

.satisfaction-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px 6px 6px;
  border: 1.5px solid var(--color-border);
  border-radius: 100px; background: #fff; width: fit-content;
}
.badge-avatars  { width: 44px; height: 28px; object-fit: cover; border-radius: 100px; }
.badge-rate     { font-size: 0.82rem; font-weight: 600; color: var(--color-text); }
.badge-divider  { display: inline-block; width: 1px; height: 14px; background: var(--color-border); }
.badge-label    { font-size: 0.82rem; font-weight: 500; color: var(--color-muted); }

.cotation-title {
  margin: 0;
  font-size: clamp(2rem, 3.6vw, 3rem);
  font-weight: 800; color: var(--color-text);
  line-height: 1.13; letter-spacing: -0.025em;
}

.cotation-desc {
  margin: 0; font-size: 0.88rem;
  color: var(--color-muted); line-height: 1.72; max-width: 470px;
}

/* ════════════════════════════════════════════════
   CARD
════════════════════════════════════════════════ */
.cotation-card {
  position: relative;
  border-radius: var(--radius-card); overflow: visible; background: #fff;
  box-shadow: 0 4px 24px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.04);
}

.card-header { background: var(--color-card-hdr); padding: 22px 28px; text-align: center; }
.card-header__title { margin: 0; font-size: 1rem; font-weight: 700; color: #fff; line-height: 1.55; }
.card-body { 
  position: relative;
  padding: 20px 20px 24px; 
  display: flex; 
  flex-direction: column; 
  gap: 10px; 
}

/* ════════════════════════════════════════════════
   FLAG-ICONS
════════════════════════════════════════════════ */
.fi {
  width: 22px !important;
  height: 15px !important;
  border-radius: 2px;
  flex-shrink: 0;
  display: inline-block;
  background-size: cover;
}

/* ════════════════════════════════════════════════
   CHAMPS
════════════════════════════════════════════════ */
.field-row  { display: flex; gap: 10px; align-items: stretch; position: relative; }
.field-group { display: flex; flex-direction: column; position: relative; }

.field-input {
  height: var(--h-field); padding: 0 14px;
  border: 1.5px solid var(--color-border); border-radius: var(--radius-field);
  font-family: var(--font); font-size: 0.875rem; color: var(--color-text);
  background: #fff; outline: none; transition: border-color 0.2s; box-sizing: border-box;
}
.field-input:focus        { border-color: var(--color-accent); }
.field-input::placeholder { color: #bbb; }
.field-input--flex        { flex: 1; width: 0; }

.field-label  { font-size: 0.78rem; font-weight: 500; color: var(--color-muted); margin-bottom: 6px; display: block; }
.field--error { border-color: var(--color-error) !important; }
.error-msg    { font-size: 0.72rem; color: var(--color-error); margin-top: 3px; padding-left: 2px; }

.field-icon-wrap { position: relative; display: flex; align-items: center; }
.field-icon      { position: absolute; left: 14px; color: #bbb; font-size: 0.82rem; pointer-events: none; z-index: 1; }
.field-input--icon { padding-left: 38px; width: 100%; }

/* ════════════════════════════════════════════════
   SELECTOR PAYS
════════════════════════════════════════════════ */
.select-country {
  position: relative; display: flex; align-items: center; gap: 6px;
  padding: 0 12px; height: var(--h-field); min-width: 90px;
  border: 1.5px solid var(--color-border); border-radius: var(--radius-field);
  background: #fff; cursor: pointer; flex-shrink: 0;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.select-country:hover {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px rgba(243, 193, 125, 0.15);
}

.select-country .fi {
  font-size: 1.2rem;
  flex-shrink: 0;
  line-height: 1;
}

/* ════════════════════════════════════════════════
   DEVISE + MONTANT
════════════════════════════════════════════════ */
.field-row--amount { gap: 10px; }

.select-currency {
  position: relative; display: flex; align-items: center; gap: 8px;
  padding: 0 12px; height: 52px; min-width: 120px;
  border: 1.5px solid var(--color-border); border-radius: var(--radius-field);
  background: #fff; cursor: pointer; flex-shrink: 0;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.select-currency:hover {
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px rgba(243, 193, 125, 0.15);
}

.select-currency .fi {
  font-size: 1.1rem;
  flex-shrink: 0;
  line-height: 1;
}

.currency-code { font-size: 0.92rem; font-weight: 700; color: var(--color-text); }

.amount-box {
  flex: 1; display: flex; align-items: center; justify-content: flex-end;
  gap: 6px; height: 52px; padding: 0 14px;
  border: 1.5px solid var(--color-border); border-radius: var(--radius-field); background: #fff;
}
.amount-input {
  flex: 1; border: none; outline: none; font-family: var(--font);
  font-size: 1.7rem; font-weight: 700; color: var(--color-text);
  text-align: right; background: transparent; width: 100%; -moz-appearance: textfield;
}
.amount-input::-webkit-outer-spin-button,
.amount-input::-webkit-inner-spin-button { -webkit-appearance: none; }
.amount-symbol { font-size: 1rem; font-weight: 600; color: var(--color-muted); opacity: 0.6; white-space: nowrap; }

/* ════════════════════════════════════════════════
   SELECT STYLISÉ
════════════════════════════════════════════════ */
.select-styled {
  position: relative; display: flex; align-items: center; justify-content: space-between;
  height: var(--h-field); padding: 0 14px;
  border: 1.5px solid var(--color-border); border-radius: var(--radius-field);
  background: #fff; cursor: pointer; user-select: none;
}
.select-styled__inner { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.select-styled__label { font-size: 0.875rem; color: #bbb; pointer-events: none; }
.select-styled__label--filled { color: var(--color-text); }

.select-hidden {
  position: absolute; inset: 0; opacity: 0;
  width: 100%; height: 100%; cursor: pointer; z-index: 2;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding: 0 12px;
  font-size: 0.875rem;
  font-family: var(--font);
}

.select-hidden option {
  padding: 12px 16px;
  line-height: 1.6;
  background: #fff;
  color: var(--color-text);
}

.select-hidden option:checked {
  background: linear-gradient(#F3C17D, #F3C17D);
  background-color: #F3C17D;
  color: #2F2113;
}

.select-full  { z-index: 2; }
.chevron-icon { font-size: 0.75rem; color: var(--color-accent); pointer-events: none; flex-shrink: 0; opacity: 0.7; }

/* ════════════════════════════════════════════════
   MÉTHODES DE PAIEMENT
════════════════════════════════════════════════ */
.payment-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-top: 8px; }

.payment-btn {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 14px 8px; border: 1.5px solid var(--color-border);
  border-radius: var(--radius-field); background: #faf8f5; cursor: pointer;
  transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
}
.payment-btn:hover { border-color: #aaa; }
.payment-btn.is-active {
  border-color: var(--color-text); background: #fff;
  box-shadow: 0 0 0 1px var(--color-text);
}
.payment-btn__icon  { font-size: 1.3rem; color: var(--color-text); line-height: 1; }
.payment-btn__label { font-family: var(--font); font-size: 0.76rem; font-weight: 600; color: var(--color-text); }

/* ════════════════════════════════════════════════
   SUBMIT
════════════════════════════════════════════════ */
.form-submit { margin-top: 6px; width: 100%; }
.form-submit :deep(button),
.form-submit :deep(a) { width: 100% !important; justify-content: center !important; }

/* ════════════════════════════════════════════════
   TOAST
════════════════════════════════════════════════ */
.toast {
  position: fixed; bottom: 28px; right: 28px; z-index: 9999;
  display: flex; align-items: center; gap: 10px;
  padding: 14px 20px; border-radius: 12px;
  font-family: var(--font); font-size: 0.875rem; font-weight: 500;
  color: #fff; box-shadow: 0 8px 32px rgba(0,0,0,0.2); max-width: 380px; line-height: 1.4;
}
.toast--info    { background: #2A1A08; }
.toast--success { background: #2e7d32; }
.toast--error   { background: #c62828; }
.toast i { font-size: 1rem; flex-shrink: 0; }
.toast-slide-enter-active, .toast-slide-leave-active { transition: all 0.35s ease; }
.toast-slide-enter-from,  .toast-slide-leave-to      { opacity: 0; transform: translateY(16px); }

/* ════════════════════════════════════════════════
   POPUP
════════════════════════════════════════════════ */
.popup-overlay {
  position: fixed; inset: 0; z-index: 10000;
  background: rgba(0,0,0,0.48);
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.popup {
  background: #fff; border-radius: 20px; padding: 40px 32px 32px;
  max-width: 440px; width: 100%; text-align: center;
  box-shadow: 0 24px 64px rgba(0,0,0,0.22);
  display: flex; flex-direction: column; align-items: center; gap: 14px;
}
.popup__icon-wrap { width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }
.popup--success .popup__icon-wrap { background: #e8f5e9; }
.popup--error   .popup__icon-wrap { background: #ffebee; }
.popup__icon { font-size: 2.2rem; }
.popup--success .popup__icon { color: #2e7d32; }
.popup--error   .popup__icon { color: #c62828; }
.popup__title   { margin: 0; font-family: var(--font); font-size: 1.2rem; font-weight: 800; color: var(--color-text); }
.popup__message { margin: 0; font-family: var(--font); font-size: 0.875rem; color: var(--color-muted); line-height: 1.65; }
.popup__close-btn {
  margin-top: 8px; padding: 13px 40px; background: var(--color-accent);
  color: #1a0e02; border: none; border-radius: 100px;
  font-family: var(--font); font-size: 0.9rem; font-weight: 700;
  cursor: pointer; transition: background 0.2s, transform 0.2s;
}
.popup__close-btn:hover { background: #d9a830; transform: translateY(-2px); }
.popup-fade-enter-active, .popup-fade-leave-active { transition: opacity 0.3s ease; }
.popup-fade-enter-from,  .popup-fade-leave-to      { opacity: 0; }

/* ════════════════════════════════════════════════
   RESPONSIVE
════════════════════════════════════════════════ */
@media (max-width: 960px) {
  .cotation-section { padding: 56px 24px 64px; }
  .cotation-inner   { grid-template-columns: 1fr; gap: 40px; }
  .cotation-desc    { max-width: 100%; }
  
  .dropdown-menu--phone {
    min-width: 260px;
  }
}

@media (max-width: 600px) {
  .cotation-section   { padding: 40px 16px 52px; }
  .card-header        { padding: 18px 16px; }
  .card-header__title { font-size: 0.92rem; }
  .card-body          { padding: 16px 14px 20px; }
  .amount-input       { font-size: 1.3rem; }
  .toast { bottom: 16px; right: 16px; left: 16px; max-width: 100%; }
  .popup { padding: 32px 20px 24px; }
  
  .dropdown-menu {
    max-height: 280px;
  }
  
  .dropdown-menu--phone {
    min-width: 240px;
    left: 0;
  }
}
</style>